export const dayNames = ['월', '화', '수', '목', '금', '토', '일'];
const weekdayNames = ['일', '월', '화', '수', '목', '금', '토'];
const pad = value => String(value).padStart(2, '0');
export const timeLabel = minute => `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;
// rule mode: 0=24시간, 1=시간 지정, 2=쉬는 날(출입 불가). Unused times keep a friendly default.
const DAY_TIME = { startMinute: 360, endMinute: 1380 };
const HOLIDAY_TIME = { startMinute: 540, endMinute: 1080 };
const withTimes = (rule, fallback) => ({ ...rule, ...(rule.mode === 1 ? { startMinute: rule.startMinute, endMinute: rule.endMinute } : fallback) });
const sameRule = (a, b) => a.mode === b.mode && (a.mode !== 1 || (a.startMinute === b.startMinute && a.endMinute === b.endMinute));
export const sameWeekdays = days => days.slice(1, 5).every(d => sameRule(d, days[0]));
export const toForm = settings => {
  const days = settings.days.map(d => withTimes(d, DAY_TIME));
  return { mode: settings.mode, days, holidays: settings.holidays.map(h => withTimes(h, HOLIDAY_TIME)), split: !sameWeekdays(days) };
};
export const newHoliday = key => ({ key, startDate: '', endDate: '', mode: 2, ...HOLIDAY_TIME });
export const openAllDays = days => days.every(d => d.mode === 0) ? days.map(() => ({ mode: 1, ...DAY_TIME })) : days;

// Hour and minute are picked separately; the end may be 24시 (minute fixed to 00).
export const hourOptions = kind => Array.from({ length: kind === 'start' ? 24 : 25 }, (_, h) => h);
// '18시 (오후 6시)' so nobody has to convert the 24-hour clock in their head.
export const hourLabel = hour => `${pad(hour)}시 (${hour === 0 ? '자정' : hour === 24 ? '밤 12시' : hour === 12 ? '낮 12시' : hour < 12 ? `오전 ${hour}시` : `오후 ${hour - 12}시`})`;
// 5-minute steps; an existing off-step value stays selectable.
export const minuteOptions = current => {
  const list = Array.from({ length: 12 }, (_, i) => i * 5);
  if (!list.includes(current)) list.push(current);
  return list.sort((a, b) => a - b);
};
export const minuteLabel = minute => `${pad(minute)}분`;

const dateNumber = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return NaN;
  const time = Date.parse(value + 'T00:00:00Z');
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time : NaN;
};
export const dateCount = (start, end) => {
  const days = (dateNumber(end) - dateNumber(start)) / 86400000 + 1;
  return Number.isFinite(days) && days > 0 ? `${days}일` : '';
};
// A special day that only shortens hours starts from that weekday's usual hours.
export const usualTimes = (form, date) => {
  const time = dateNumber(date);
  const rule = form.mode === 0 ? { mode: 0 } : Number.isFinite(time) ? form.days[(new Date(time).getUTCDay() + 6) % 7] : { mode: 2 };
  return rule.mode === 1 ? { startMinute: rule.startMinute, endMinute: rule.endMinute } : rule.mode === 0 ? { startMinute: 0, endMinute: 1440 } : { ...HOLIDAY_TIME };
};
export const todayValue = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
const dateLabel = value => `${value.replace(/-/g, '.')}(${weekdayNames[new Date(dateNumber(value)).getUTCDay()]})`;
export const periodLabel = (start, end) => {
  if (start === end) return dateLabel(start);
  return `${dateLabel(start)} ~ ${start.slice(0, 4) === end.slice(0, 4) ? dateLabel(end).slice(5) : dateLabel(end)}`;
};

const ruleOut = rule => rule.mode === 1
  ? { mode: 1, startMinute: rule.startMinute, endMinute: rule.endMinute }
  : { mode: rule.mode, startMinute: 0, endMinute: rule.mode === 0 ? 1440 : 0 };
const timeError = rule => rule.mode === 1 && !(rule.startMinute < rule.endMinute) ? '끝나는 시간은 시작 시간보다 늦어야 해요. 밤 12시(24:00)를 넘길 수는 없으니, 새벽까지 여는 날은 끝나는 시간을 24:00으로 하거나 ‘24시간’을 골라 주세요.' : '';

// Returns server settings plus per-row messages so mistakes are shown next to the row.
export function readForm(form) {
  const dayErrors = form.days.map(d => form.mode === 1 ? timeError(d) : '');
  const holidayErrors = {};
  form.holidays.forEach(h => {
    const start = dateNumber(h.startDate), end = dateNumber(h.endDate);
    if (!Number.isFinite(start) || !Number.isFinite(end)) holidayErrors[h.key] = '날짜를 골라 주세요.';
    else if (end < start) holidayErrors[h.key] = '끝나는 날이 시작하는 날보다 빠를 수 없어요.';
    else if ((end - start) / 86400000 >= 128) holidayErrors[h.key] = '한 번에 최대 128일까지 정할 수 있어요.';
    else holidayErrors[h.key] = timeError(h);
  });
  const holidays = form.holidays.map(h => ({ key: h.key, startDate: h.startDate, endDate: h.endDate, ...ruleOut(h) }))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  holidays.forEach((h, i) => {
    const prev = holidays[i - 1];
    if (!prev || holidayErrors[h.key] || holidayErrors[prev.key] || h.startDate > prev.endDate) return;
    holidayErrors[h.key] = holidayErrors[prev.key] = '다른 특별한 날과 날짜가 겹쳐요.';
  });
  // mode is null until the owner picks one when the device schedule could not be read.
  const error = (form.mode === null ? '평소 출입 방식을 골라 주세요.' : '') || dayErrors.find(Boolean) || Object.values(holidayErrors).find(Boolean) || '';
  const days = form.mode === 0 ? form.days.map(() => ruleOut({ mode: 0 })) : form.days.map(ruleOut);
  return { settings: error ? null : { mode: form.mode, days, holidays }, dayErrors, holidayErrors, error };
}

export const ruleText = rule => rule.mode === 0 ? '24시간' : rule.mode === 2 ? '쉬는 날' : `${timeLabel(rule.startMinute)}~${timeLabel(rule.endMinute)}`;
const dayRange = (from, to) => from === to ? dayNames[from] : to - from === 1 ? `${dayNames[from]}·${dayNames[to]}` : `${dayNames[from]}~${dayNames[to]}`;
// Groups neighbouring days with the same hours: [{ days: '월~금', text: '06:00~23:00' }, ...]
export function weekSummary(settings) {
  const texts = settings.days.map(d => ruleText(settings.mode === 0 ? { mode: 0 } : d));
  if (texts.every(t => t === texts[0])) return [{ days: '매일', text: texts[0] }];
  const groups = [];
  texts.forEach((text, i) => {
    const last = groups[groups.length - 1];
    if (last && last.text === text) last.to = i;
    else groups.push({ from: i, to: i, text });
  });
  return groups.map(g => ({ days: dayRange(g.from, g.to), text: g.text }));
}
